import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Input,
  Space,
  Modal,
  Form,
  message,
  Tag,
  Select,
  Card,
  Typography,
  Tooltip,
  Table,
  Tabs,
  Popconfirm,
  InputNumber,
  Switch,
  Result,
  Spin,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  StopOutlined,
  SafetyOutlined,
  GlobalOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { blacklistService, BlacklistEntry, BlacklistConfig, BlacklistType, BlacklistStatus } from '../services/blacklist';
import { useAuthStore } from '../stores/authStore';
import { useFeatureStore } from '../stores/featureStore';

const { Title, Text } = Typography;
const { Option } = Select;

const BlacklistManagement: React.FC = () => {
  const { user } = useAuthStore();
  const { hasBlacklistFeature, loaded: featuresLoaded, fetchFeatures } = useFeatureStore();

  // 加载功能列表
  useEffect(() => {
    if (user && user.user_type !== 'admin' && !featuresLoaded) {
      // 客户调用 getMyFeatures，不需要传 client_id
      fetchFeatures();
    }
  }, [user, featuresLoaded, fetchFeatures]);

  // 非管理员且功能未开通时显示提示
  if (user && user.user_type !== 'admin') {
    if (!featuresLoaded) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Spin size="large" tip="加载中..." />
        </div>
      );
    }
    if (!hasBlacklistFeature()) {
      return (
        <Result
          status="403"
          title="功能未开通"
          subTitle="您尚未开通黑名单管理功能，请联系管理员开通。"
        />
      );
    }
  }
  // 列表状态
  const [entries, setEntries] = useState<BlacklistEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 筛选状态
  const [filterType, setFilterType] = useState<BlacklistType | ''>('');
  const [filterStatus, setFilterStatus] = useState<BlacklistStatus | ''>('active');
  const [searchIdentifier, setSearchIdentifier] = useState('');
  const [searchClientId, setSearchClientId] = useState<number | undefined>(undefined);

  // 是否为管理员
  const isAdmin = user?.user_type === 'admin';

  // 配置状态
  const [configs, setConfigs] = useState<BlacklistConfig[]>([]);
  const [configLoading, setConfigLoading] = useState(false);

  // 弹窗状态
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [configForm] = Form.useForm();

  // 加载黑名单列表
  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await blacklistService.listEntries({
        client_id: isAdmin ? searchClientId : undefined,
        type: filterType || undefined,
        status: filterStatus || undefined,
        identifier: searchIdentifier || undefined,
        page: currentPage,
        page_size: pageSize,
      });
      if (response.success) {
        setEntries(response.data.list || []);
        setTotal(response.data.total);
      }
    } catch (error) {
      message.error('加载黑名单列表失败');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, searchIdentifier, searchClientId, currentPage, pageSize, isAdmin]);

  // 加载配置
  const loadConfigs = useCallback(async () => {
    setConfigLoading(true);
    try {
      const response = await blacklistService.getConfig();
      if (response.success) {
        setConfigs(response.data || []);
      }
    } catch (error) {
      message.error('加载配置失败');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // 添加黑名单
  const handleAdd = async (values: { blacklist_type: BlacklistType; identifier: string; remark?: string }) => {
    try {
      const response = await blacklistService.addEntry(values);
      if (response.success) {
        message.success('添加成功');
        setAddModalVisible(false);
        addForm.resetFields();
        loadEntries();
      } else {
        message.error(response.message || '添加失败');
      }
    } catch (error) {
      message.error('添加失败');
    }
  };

  // 移除黑名单
  const handleRemove = async (id: number) => {
    try {
      const response = await blacklistService.removeEntry(id);
      if (response.success) {
        message.success('移除成功');
        loadEntries();
      } else {
        message.error(response.message || '移除失败');
      }
    } catch (error) {
      message.error('移除失败');
    }
  };

  // 更新统一配置
  const handleUpdateConfig = async (values: {
    window_minutes: number;
    threshold: number;
    block_duration: number;
    fingerprint_enabled: boolean;
    ip_enabled: boolean;
  }) => {
    try {
      // 同时更新两种类型的配置
      const updatePromises = [
        blacklistService.updateConfig({
          blacklist_type: 'fingerprint',
          window_minutes: values.window_minutes,
          threshold: values.threshold,
          block_duration: values.block_duration,
          enabled: values.fingerprint_enabled,
        }),
        blacklistService.updateConfig({
          blacklist_type: 'ip',
          window_minutes: values.window_minutes,
          threshold: values.threshold,
          block_duration: values.block_duration,
          enabled: values.ip_enabled,
        }),
      ];
      
      const results = await Promise.all(updatePromises);
      if (results.every(r => r.success)) {
        message.success('配置更新成功');
        setConfigModalVisible(false);
        loadConfigs();
      } else {
        message.error('部分配置更新失败');
      }
    } catch (error) {
      message.error('更新失败');
    }
  };

  // 打开统一配置弹窗
  const openConfigModal = () => {
    const fingerprintConfig = configs.find(c => c.blacklist_type === 'fingerprint');
    const ipConfig = configs.find(c => c.blacklist_type === 'ip');
    
    // 使用任一配置的参数作为默认值（它们应该是一样的）
    const baseConfig = fingerprintConfig || ipConfig;
    configForm.setFieldsValue({
      window_minutes: baseConfig?.window_minutes ?? 5,
      threshold: baseConfig?.threshold ?? 10,
      block_duration: baseConfig?.block_duration ?? 0,
      fingerprint_enabled: fingerprintConfig?.enabled ?? true,
      ip_enabled: ipConfig?.enabled ?? true,
    });
    setConfigModalVisible(true);
  };

  // 统一配置卡片
  const renderUnifiedConfigCard = () => {
    const fingerprintConfig = configs.find(c => c.blacklist_type === 'fingerprint');
    const ipConfig = configs.find(c => c.blacklist_type === 'ip');
    const baseConfig = fingerprintConfig || ipConfig;
    
    return (
      <Card
        title={
          <Space>
            <StopOutlined style={{ color: '#f5222d' }} />
            <span>自动封禁配置</span>
          </Space>
        }
        extra={
          <Button type="link" icon={<SettingOutlined />} onClick={openConfigModal}>
            配置
          </Button>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* 启用状态 */}
          <div>
            <Text strong>启用状态</Text>
            <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
              <Space>
                <SafetyOutlined style={{ color: '#722ed1' }} />
                <Text>指纹黑名单:</Text>
                <Tag color={(fingerprintConfig?.enabled ?? true) ? 'green' : 'default'}>
                  {(fingerprintConfig?.enabled ?? true) ? '已启用' : '已禁用'}
                </Tag>
              </Space>
              <Space>
                <GlobalOutlined style={{ color: '#1890ff' }} />
                <Text>IP黑名单:</Text>
                <Tag color={(ipConfig?.enabled ?? true) ? 'green' : 'default'}>
                  {(ipConfig?.enabled ?? true) ? '已启用' : '已禁用'}
                </Tag>
              </Space>
            </div>
          </div>
          
          {/* 共用参数 */}
          <div>
            <Text strong>封禁规则</Text>
            <div style={{ display: 'flex', gap: 48, marginTop: 8 }}>
              <Space>
                <Text type="secondary">窗口时间:</Text>
                <Text>{baseConfig?.window_minutes ?? 5} 分钟</Text>
              </Space>
              <Space>
                <Text type="secondary">触发阈值:</Text>
                <Text>{baseConfig?.threshold ?? 10} 次</Text>
              </Space>
              <Space>
                <Text type="secondary">封禁时长:</Text>
                <Text>{(baseConfig?.block_duration ?? 0) === 0 ? '永久' : `${baseConfig?.block_duration} 天`}</Text>
              </Space>
            </div>
          </div>
        </Space>
      </Card>
    );
  };

  // 表格列定义
  const columns = [
    // 客户ID列（仅管理员可见）
    ...(isAdmin ? [{
      title: '客户ID',
      dataIndex: 'client_id',
      key: 'client_id',
      width: 90,
      render: (clientId: number) => <Tag color="cyan">{clientId}</Tag>,
    }] : []),
    {
      title: '类型',
      dataIndex: 'blacklist_type',
      key: 'blacklist_type',
      width: 100,
      render: (type: BlacklistType) => (
        <Tag color={type === 'fingerprint' ? 'purple' : 'blue'} icon={type === 'fingerprint' ? <SafetyOutlined /> : <GlobalOutlined />}>
          {type === 'fingerprint' ? '指纹' : 'IP'}
        </Tag>
      ),
    },
    {
      title: '标识',
      dataIndex: 'identifier',
      key: 'identifier',
      width: 280,
      ellipsis: true,
      render: (identifier: string) => (
        <Tooltip title={identifier}>
          <Text copyable style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {identifier}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 120,
      render: (reason: string) => {
        const reasonMap: Record<string, { label: string; color: string }> = {
          auto_frequency: { label: '频率限制', color: 'orange' },
          manual: { label: '手动添加', color: 'blue' },
        };
        const config = reasonMap[reason] || { label: reason, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '访问次数',
      dataIndex: 'access_count',
      key: 'access_count',
      width: 100,
      render: (count: number) => count > 0 ? <Tag color="red">{count}</Tag> : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: BlacklistStatus) => {
        const statusMap: Record<BlacklistStatus, { label: string; color: string }> = {
          active: { label: '生效中', color: 'red' },
          expired: { label: '已过期', color: 'default' },
          removed: { label: '已解除', color: 'green' },
        };
        const config = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '封禁时间',
      dataIndex: 'blocked_at',
      key: 'blocked_at',
      width: 180,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 180,
      render: (time: string | null) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : <Tag color="red">永久</Tag>,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      ellipsis: true,
      render: (remark: string) => remark || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, record: BlacklistEntry) => (
        record.status === 'active' ? (
          <Popconfirm
            title="确定要解除此黑名单吗？"
            onConfirm={() => handleRemove(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              解除
            </Button>
          </Popconfirm>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        <StopOutlined style={{ marginRight: 8 }} />
        黑名单管理
      </Title>

      <Tabs
        defaultActiveKey="list"
        items={[
          {
            key: 'list',
            label: '黑名单列表',
            children: (
              <>
                {/* 筛选栏 */}
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Space wrap>
                    {isAdmin && (
                      <InputNumber
                        placeholder="客户ID"
                        value={searchClientId}
                        onChange={(value) => setSearchClientId(value || undefined)}
                        style={{ width: 100 }}
                        min={1}
                      />
                    )}
                    <Select
                      placeholder="类型"
                      value={filterType}
                      onChange={setFilterType}
                      style={{ width: 120 }}
                      allowClear
                    >
                      <Option value="fingerprint">指纹</Option>
                      <Option value="ip">IP</Option>
                    </Select>
                    <Select
                      placeholder="状态"
                      value={filterStatus}
                      onChange={setFilterStatus}
                      style={{ width: 120 }}
                      allowClear
                    >
                      <Option value="active">生效中</Option>
                      <Option value="expired">已过期</Option>
                      <Option value="removed">已解除</Option>
                    </Select>
                    <Input
                      placeholder="搜索标识"
                      value={searchIdentifier}
                      onChange={e => setSearchIdentifier(e.target.value)}
                      style={{ width: 200 }}
                      prefix={<SearchOutlined />}
                      allowClear
                    />
                    <Button icon={<SearchOutlined />} onClick={() => { setCurrentPage(1); loadEntries(); }}>
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={loadEntries}>
                      刷新
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
                      添加黑名单
                    </Button>
                  </Space>
                </Card>

                {/* 列表 */}
                <Table
                  dataSource={entries}
                  columns={columns}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 1400 }}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (t) => `共 ${t} 条`,
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    },
                  }}
                />
              </>
            ),
          },
          {
            key: 'config',
            label: '规则配置',
            children: (
              <div style={{ maxWidth: 800 }}>
                {renderUnifiedConfigCard()}
              </div>
            ),
          },
        ]}
      />

      {/* 添加黑名单弹窗 */}
      <Modal
        title="添加黑名单"
        open={addModalVisible}
        onCancel={() => { setAddModalVisible(false); addForm.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            name="blacklist_type"
            label="类型"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select placeholder="请选择类型">
              <Option value="fingerprint">指纹</Option>
              <Option value="ip">IP</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="identifier"
            label="标识"
            rules={[{ required: true, message: '请输入标识' }]}
            extra="指纹类型输入 visitor_id，IP类型输入 IP 地址"
          >
            <Input placeholder="请输入指纹ID或IP地址" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea placeholder="请输入备注（可选）" rows={3} />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setAddModalVisible(false); addForm.resetFields(); }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 统一配置弹窗 */}
      <Modal
        title="自动封禁配置"
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        footer={null}
        destroyOnClose
        width={500}
      >
        <Form form={configForm} layout="vertical" onFinish={handleUpdateConfig}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Text strong style={{ marginBottom: 12, display: 'block' }}>启用状态</Text>
            <Space size="large">
              <Space>
                <Form.Item
                  name="fingerprint_enabled"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
                <SafetyOutlined style={{ color: '#722ed1' }} />
                <Text>指纹黑名单</Text>
              </Space>
              <Space>
                <Form.Item
                  name="ip_enabled"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
                <GlobalOutlined style={{ color: '#1890ff' }} />
                <Text>IP黑名单</Text>
              </Space>
            </Space>
          </Card>
          
          <Form.Item
            name="window_minutes"
            label="窗口时间（分钟）"
            rules={[{ required: true, message: '请输入窗口时间' }]}
            extra="在此时间窗口内的访问次数将被累计"
          >
            <InputNumber min={1} max={60} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="threshold"
            label="触发阈值（次）"
            rules={[{ required: true, message: '请输入触发阈值' }]}
            extra="窗口内访问达到此次数将触发封禁"
          >
            <InputNumber min={1} max={1000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="block_duration"
            label="封禁时长（天）"
            rules={[{ required: true, message: '请输入封禁时长' }]}
            extra="0 表示永久封禁，最多 90 天（3个月）"
          >
            <InputNumber min={0} max={90} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setConfigModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BlacklistManagement;
